import { EditorState, Plugin, PluginKey } from "@knowt/editor/pm/state";
import { DecorationSet } from "@knowt/editor/pm/view";
import { Editor, Extension, generateText, getTextSerializersFromSchema } from "@knowt/editor/react";
import { Root, createRoot } from "react-dom/client";

type CustomPlaceholderProps = {
    placeholder: (props: { editor: Editor }) => React.ReactElement<any>;
};

const CUSTOMPLACEHOLDER_ID = "custom-placeholder";

const customPlaceholderPluginKey = new PluginKey("customPlaceholder");

export const CustomPlaceholder = Extension.create<
    CustomPlaceholderProps,
    { customPlaceholderStorage: { root: Root | null } }
>({
    name: "custom-placeholder",

    addStorage() {
        return {
            customPlaceholderStorage: {
                root: null,
            },
        };
    },

    addProseMirrorPlugins() {
        const getStorage = () => this.storage.customPlaceholderStorage;

        const shouldShowPlaceholder = (state: EditorState) => {
            if (this.editor.isFocused || state.doc.childCount > 1 || state.doc.textContent) {
                return false;
            }

            const docHiddenTextContent = generateText(state.doc.toJSON(), this.editor.extensionManager.extensions, {
                textSerializers: getTextSerializersFromSchema(this.editor.schema),
            });

            if (document.activeElement && this.editor.view.dom?.parentElement?.contains?.(document.activeElement)) {
                return false;
            }

            return !docHiddenTextContent;
        };

        const instantiateRoot = () => {
            const storage = getStorage();

            const editorEl = this.editor.view.nodeDOM(0)?.parentElement?.parentElement;
            if (!editorEl) return;

            const el = document.getElementById(CUSTOMPLACEHOLDER_ID);
            if (el) return;

            const div = document.createElement("div");
            div.id = CUSTOMPLACEHOLDER_ID;

            editorEl.append(div);
            storage.root = createRoot(div);
        };

        const applyCustomPlaceholder = () => {
            const storage = getStorage();

            if (!storage.root) return;
            const placeholderComponent = this.options.placeholder({ editor: this.editor });
            storage.root.render(placeholderComponent);
        };

        const disapplyCustomPlaceholder = () => {
            const storage = getStorage();
            if (!storage.root) return;
            const el = document.getElementById(CUSTOMPLACEHOLDER_ID);
            el?.remove();
        };

        return [
            new Plugin({
                key: customPlaceholderPluginKey,
                state: {
                    init() {
                        return DecorationSet.empty;
                    },
                    apply(_tr, _value, _oldState, newState) {
                        if (shouldShowPlaceholder(newState)) {
                            instantiateRoot();
                            applyCustomPlaceholder();
                        } else {
                            disapplyCustomPlaceholder();
                        }

                        return DecorationSet.empty;
                    },
                },
                props: {
                    decorations(state) {
                        return this.getState(state);
                    },
                },
            }),
        ];
    },
});
